/**
 * Paragraphs Entry.
 */
import React, { lazy, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import Loader from '@components/loader';
import { TranslationProvider } from '@context/translation-context';
import { sanitizeJson } from '@services/string-transformation-services';

const ImageComparisonSlider = lazy(
  () =>
    import(
      /* webpackChunkName: "image-comparison-slider" */ './image-comparison-slider'
    )
);

const CategoryParagraph = lazy(
  () => import(/* webpackChunkName: "related-paragraph" */ './related')
);

const RotatingBannerContainer = lazy(
  () => import(/* webpackChunkName: "rotating-banner" */ './rotating-banner')
);

const CountdownBannerWrapper = lazy(
  () =>
    import(
      /* webpackChunkName: "countdown-banner" */ './banners/countdown-banner-wrapper'
    )
);

/**
 * Start Loading Paragraphs.
 */
export const paragraphs = (): void => {
  const paragraphs: NodeListOf<HTMLElement> = document.querySelectorAll(
    '.varian-paragraph-wrapper, .varian-banner-wrapper, .countdown-banner'
  );

  let paragraphCount = 0;
  if (paragraphs.length > 0) {
    paragraphs.forEach((paragraph: HTMLElement): void => {
      const paragraphData: NodeListOf<HTMLElement> = paragraph.querySelectorAll(
        '.varian-paragraph-data, .varian-banner-data'
      );
      const paragraphContainer: NodeListOf<HTMLElement> =
        paragraph.querySelectorAll(
          '.varian-paragraph-container, .varian-banner-container'
        );

      if (paragraphData.length > 0 && paragraphContainer.length > 0) {
        paragraphCount++;

        const paragraphJson = sanitizeJson(paragraphData[0].innerHTML);
        const { paragraphStyle, bannerStyle } = paragraphJson;

        /**
         * Set the correct style type and paragraph id.  All paragraphs are
         * very similar but banners are treated a little differently since
         * they're the oldest.
         */
        const styleType = paragraphStyle || bannerStyle;
        if (paragraphContainer[0]?.className.indexOf('banner') > -1) {
          paragraphJson.bannerId = `varian-banner-${paragraphCount}`;
        } else {
          paragraphJson.paragraphId = `varian-paragraph-${paragraphCount}`;
        }

        const paragraphType = getParagraphType(styleType, paragraphJson);

        if (paragraphType) {
          const root = createRoot(paragraphContainer[0]);
          root.render(
            <Suspense fallback={<Loader />}>
              <TranslationProvider>{paragraphType}</TranslationProvider>
            </Suspense>
          );
        }
      }
    });
  }
};

/**
 * Get Paragraph Type.
 */
const getParagraphType = (
  paragraphType: string,
  paragraphJson
): JSX.Element | null => {
  switch (true) {
    case paragraphType === 'countdown-banner':
      return <CountdownBannerWrapper paragraphData={paragraphJson} />;
    case paragraphType === 'category-related':
    case paragraphType === 'custom-card-container':
      return <CategoryParagraph paragraphData={paragraphJson} />;
    case paragraphType === 'rotating-banner':
      return <RotatingBannerContainer paragraphData={paragraphJson} />;
    case paragraphType === 'image-comparison-slider':
      return <ImageComparisonSlider paragraphData={paragraphJson} />;
    default:
      return null;
  }
};

/**
 * Build Image Comparison Slider (for manual entries).
 */
export const imageComparisonSlider = () => {
  const imgComparisonSlider = document.querySelectorAll(
    '.image-comparison-slider'
  ) as NodeListOf<HTMLElement>;

  if (imgComparisonSlider) {
    imgComparisonSlider.forEach((fields, index) => {
      const altTextImageA = fields.querySelector('.alt-tag-a')?.innerHTML;
      const altTextImageB = fields.querySelector('.alt-tag-b')?.innerHTML;

      const {
        aspectRatio,
        imageA,
        imageB,
        paragraphId,
        paragraphStyle,
        sliderPosition,
        verticalSlider,
        imageWidth,
      } = fields.dataset;

      if (Object.keys(fields.dataset).length > 0) {
        const root = createRoot(imgComparisonSlider[index]);
        root.render(
          <Suspense fallback={<Loader />}>
            <TranslationProvider>
              <ImageComparisonSlider
                paragraphData={{
                  aspectRatio,
                  imageA,
                  altTextImageA,
                  imageB,
                  altTextImageB,
                  paragraphId,
                  paragraphStyle,
                  sliderPosition,
                  verticalSlider,
                  imageWidth,
                }}
              />
            </TranslationProvider>
          </Suspense>
        );
      }
    });
  }
};
